import { useState } from 'react'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import type {
  GridSelection,
  Row,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import {
  fromCellKey,
  toCellKey,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'
import {
  useHeaderDragSelection,
  type GridSelectionState,
  type HeaderDragAxis,
  type SelectionModifiers,
} from 'modules/shift_management/hooks/gridTable/selection/useHeaderDragSelection'
import { useEventSelection } from 'modules/shift_management/hooks/gridTable/selection/useEventSelection'
import {
  buildCellRangeSelection,
  buildColumnCellsExceptSelection,
  buildColumnRangeKeys,
  buildColumnSelection,
  buildRowCellsExceptSelection,
  buildRowRangeKeys,
  buildSelectedColumnIndexes,
  buildSelectedRowIds,
  buildSingleCellSelection,
  getCellRangeEndpoint,
  getColumnRangeEndpoint,
  getRowRangeEndpoint,
  toggleCellSelection,
} from 'modules/shift_management/utils/TimeGridTable/selection/builds'
import {
  areSetsEqual,
  isCellSelectable as checkIsCellSelectable,
  countSelectableRows,
  isRowSelectable,
} from 'modules/shift_management/utils/TimeGridTable/selection/checks'

interface UseGridSelectionProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  rowIndexByEntityId: Map<string, number>
  isCellSelectable?: (entity: TEntity, slot: TimeSlot) => boolean
  onKeyboardEndpointMoved?: ({ rowIndex }: { rowIndex: number }) => void
}

export type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight'

const isArrowKey = (key: string): key is ArrowKey => {
  return key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight'
}

function useGridSelection<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  rows,
  slots,
  rowIndexByEntityId,
  isCellSelectable,
  onKeyboardEndpointMoved,
}: UseGridSelectionProps<TEntity, TEvent>): {
  selection: GridSelection
  selectedCellKeys: Set<string>
  selectedRowIds: Set<string> | null
  selectedColumnIndexes: Set<number> | null
  selectedEventKeys: Set<string>
  clearSelection: () => void
  handleCellSelected: (entity: TEntity, slotIndex: number, modifiers: SelectionModifiers) => void
  handleRowHeaderSelected: (entity: TEntity, modifiers?: SelectionModifiers) => void
  handleColumnHeaderSelected: (columnIndex: number, modifiers: SelectionModifiers) => void
  handleHeaderDragStart: (params: {
    axis: HeaderDragAxis
    index: number
    modifiers: SelectionModifiers
    isPrimaryButtonPressed?: boolean
  }) => void
  handleKeyboardRangeSelection: (params: { key: string; shiftKey: boolean }) => boolean
  handleEventSelected: (entityId: string, eventId: string, modifiers: SelectionModifiers) => void
  handleCornerCellSelected: () => void
  isAllSelected: boolean
  fromCellKey: (key: string) => { entityId: string; slotIndex: number }
} {
  const [gridState, setGridState] = useState<GridSelectionState>({
    selection: { kind: 'none' },
    rowAnchor: null,
    columnAnchor: null,
    suppressedHeaderClickAxis: null,
    headerPress: null,
  })
  const { selection, headerPress } = gridState

  const { handleHeaderDragStart } = useHeaderDragSelection({
    rows,
    headerPress,
    setGridState,
  })

  const selectableRowCount = useUnsafeMemo(() => countSelectableRows(rows), [rows])
  const canSelectAll = selectableRowCount > 0 && slots.length > 0

  const isAllSelected = selection.kind === 'all'

  const setSelection = useUnsafeCallback(
    (
      nextSelection: GridSelection | ((prev: GridSelection) => GridSelection),
      resetColumnAnchor = false
    ) => {
      setGridState((prev) => ({
        ...prev,
        columnAnchor: resetColumnAnchor ? null : prev.columnAnchor,
        selection:
          typeof nextSelection === 'function' ? nextSelection(prev.selection) : nextSelection,
      }))
    },
    []
  )

  const checkSelectable = useUnsafeCallback(
    (entityId: string): boolean => {
      const rowIndex = rowIndexByEntityId.get(entityId)
      const row = rowIndex === undefined ? undefined : rows[rowIndex]

      return isRowSelectable(row)
    },
    [rows, rowIndexByEntityId]
  )

  const { selectedEventKeys, handleEventSelected } = useEventSelection({
    selection,
    checkSelectable,
    setSelection,
  })

  const checkCellSelectable = useUnsafeCallback(
    (entityId: string, slotIndex: number): boolean => {
      return checkIsCellSelectable(
        entityId,
        slotIndex,
        rows,
        slots,
        rowIndexByEntityId,
        isCellSelectable
      )
    },
    [isCellSelectable, rowIndexByEntityId, rows, slots]
  )

  const selectedCellKeys = useUnsafeMemo(() => {
    return selection.kind === 'cells' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const selectedRowIds = useUnsafeMemo(() => buildSelectedRowIds({ selection }), [selection])

  const selectedColumnIndexes = useUnsafeMemo(
    () => buildSelectedColumnIndexes(selection),
    [selection]
  )

  const clearSelection = useUnsafeCallback(() => {
    setGridState((prev) => ({
      ...prev,
      selection: { kind: 'none' },
      rowAnchor: null,
      columnAnchor: null,
    }))
  }, [])

  const handleCellSelected = useUnsafeCallback(
    (
      entity: TEntity,
      slotIndex: number,
      modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
    ) => {
      if (!checkCellSelectable(entity.id, slotIndex)) return

      const cell = { entityId: entity.id, slotIndex }
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey
      const anchorCell = selection.kind === 'cells' ? selection.anchor : null
      const resetColumnAnchor = { columnAnchor: null }

      if (modifiers.shiftKey && !anchorCell) {
        setSelection(buildSingleCellSelection(cell), resetColumnAnchor)
        return
      }

      if (modifiers.shiftKey && anchorCell) {
        setSelection(
          (prev) =>
            buildCellRangeSelection({
              anchorCell,
              targetCell: cell,
              append: isMetaPressed,
              previousSelection: prev,
              rows,
              rowIndexByEntityId,
              isCellSelectable: checkCellSelectable,
            }),
          resetColumnAnchor
        )
        return
      }

      if (isMetaPressed && (selectedRowIds?.has(entity.id) ?? false)) {
        setSelection(
          buildRowCellsExceptSelection({
            rowIds: selectedRowIds ?? new Set<string>(),
            excludedCell: cell,
            slots,
            isCellSelectable: checkCellSelectable,
          }),
          resetColumnAnchor
        )
        return
      }

      if (isMetaPressed && (selectedColumnIndexes?.has(slotIndex) ?? false)) {
        setSelection(
          buildColumnCellsExceptSelection({
            columnIndexes: selectedColumnIndexes ?? new Set<number>(),
            excludedCell: cell,
            rows,
            isCellSelectable: checkCellSelectable,
          }),
          resetColumnAnchor
        )
        return
      }

      if (isMetaPressed) {
        setSelection((prev) => toggleCellSelection(prev, cell), resetColumnAnchor)
        return
      }

      if (selection.kind === 'cells' && selection.keys.size === 1) {
        const key = toCellKey(entity.id, slotIndex)
        if (selection.keys.has(key)) {
          clearSelection()
          return
        }
      }

      setSelection(buildSingleCellSelection(cell), resetColumnAnchor)
    },
    [
      checkCellSelectable,
      clearSelection,
      rowIndexByEntityId,
      rows,
      selection,
      selectedColumnIndexes,
      selectedRowIds,
      setSelection,
      slots,
    ]
  )

  const handleRowHeaderSelected = useUnsafeCallback(
    (
      entity: TEntity,
      modifiers: SelectionModifiers = {
        shiftKey: false,
        metaKey: false,
        ctrlKey: false,
      }
    ) => {
      const rowIndex = rowIndexByEntityId.get(entity.id)
      if (rowIndex === undefined) return

      const row = rows[rowIndex]
      if (!row || !isRowSelectable(row)) return
      const rowId = row.entity.id

      setGridState((prev) => {
        if (prev.suppressedHeaderClickAxis === 'row') {
          return {
            ...prev,
            suppressedHeaderClickAxis: null,
          }
        }

        if (modifiers.shiftKey && prev.rowAnchor !== null) {
          const startIndex = Math.min(prev.rowAnchor, rowIndex)
          const endIndex = Math.max(prev.rowAnchor, rowIndex)

          if (startIndex === endIndex) {
            if (prev.selection.kind === 'row' && prev.selection.rowId === rowId) {
              return {
                ...prev,
                selection: { kind: 'none' },
                rowAnchor: null,
              }
            }

            return {
              ...prev,
              selection: { kind: 'row', rowId },
              rowAnchor: rowIndex,
            }
          }

          return {
            ...prev,
            selection: { kind: 'rowRange', keys: buildRowRangeKeys(startIndex, endIndex, rows) },
          }
        }

        const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey

        if (isMetaPressed) {
          const selectedRows =
            buildSelectedRowIds({ selection: prev.selection }) ?? new Set<string>()

          if (selectedRows.has(rowId)) {
            selectedRows.delete(rowId)
          } else {
            selectedRows.add(rowId)
          }

          if (selectedRows.size === 0) {
            return {
              ...prev,
              selection: { kind: 'none' },
              rowAnchor: null,
            }
          }

          if (selectedRows.size === 1) {
            const [selectedRowId] = selectedRows

            return {
              ...prev,
              selection: { kind: 'row', rowId: selectedRowId ?? rowId },
              rowAnchor: rowIndex,
            }
          }

          return {
            ...prev,
            selection: { kind: 'rowRange', keys: selectedRows },
            rowAnchor: prev.rowAnchor ?? rowIndex,
          }
        }

        if (prev.selection.kind === 'row' && prev.selection.rowId === rowId) {
          return {
            ...prev,
            selection: { kind: 'none' },
            rowAnchor: null,
          }
        }

        return {
          ...prev,
          selection: { kind: 'row', rowId },
          rowAnchor: rowIndex,
        }
      })
    },
    [rowIndexByEntityId, rows]
  )

  const handleColumnHeaderSelected = useUnsafeCallback(
    (columnIndex: number, modifiers: SelectionModifiers) => {
      setGridState((prev) => {
        if (prev.suppressedHeaderClickAxis === 'column') {
          return {
            ...prev,
            suppressedHeaderClickAxis: null,
          }
        }

        if (modifiers.shiftKey && prev.columnAnchor !== null) {
          const startIndex = Math.min(prev.columnAnchor, columnIndex)
          const endIndex = Math.max(prev.columnAnchor, columnIndex)

          if (startIndex !== endIndex) {
            return {
              ...prev,
              selection: { kind: 'columnRange', keys: buildColumnRangeKeys(startIndex, endIndex) },
            }
          }
        }

        const columnSelection = buildColumnSelection({
          columnIndex,
          rows,
          selectableRowCount,
          isCellSelectable: checkCellSelectable,
        })
        if (!columnSelection) return prev

        const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey

        if (isMetaPressed && columnSelection.selectsWholeColumn) {
          const selectedColumns = buildSelectedColumnIndexes(prev.selection) ?? new Set<number>()

          if (selectedColumns.has(columnIndex)) {
            selectedColumns.delete(columnIndex)
          } else {
            selectedColumns.add(columnIndex)
          }

          if (selectedColumns.size === 0) {
            return {
              ...prev,
              selection: { kind: 'none' },
              columnAnchor: null,
            }
          }

          if (selectedColumns.size === 1) {
            const [selectedColumnIndex] = selectedColumns

            return {
              ...prev,
              selection: { kind: 'column', columnIndex: selectedColumnIndex ?? columnIndex },
              columnAnchor: selectedColumnIndex ?? columnIndex,
            }
          }

          return {
            ...prev,
            selection: { kind: 'columnRange', keys: selectedColumns },
            columnAnchor: prev.columnAnchor ?? columnIndex,
          }
        }

        if (columnSelection.selectsWholeColumn) {
          if (prev.selection.kind === 'column' && prev.selection.columnIndex === columnIndex) {
            return {
              ...prev,
              selection: { kind: 'none' },
              columnAnchor: null,
            }
          }

          return {
            ...prev,
            selection: columnSelection.selection,
            columnAnchor: columnIndex,
          }
        }

        if (
          prev.selection.kind === 'cells' &&
          columnSelection.selection.kind === 'cells' &&
          areSetsEqual(prev.selection.keys, columnSelection.selection.keys)
        ) {
          return {
            ...prev,
            selection: { kind: 'none' },
            columnAnchor: null,
          }
        }
        return {
          ...prev,
          selection: columnSelection.selection,
          columnAnchor: columnIndex,
        }
      })
    },
    [checkCellSelectable, rows, selectableRowCount]
  )

  const canHandleKeyboardRangeSelection = useUnsafeCallback(
    ({ key, shiftKey }: { key: string; shiftKey: boolean }) => {
      if (!shiftKey || !isArrowKey(key)) return false
      if (
        selection.kind === 'none' ||
        selection.kind === 'all' ||
        selection.kind === 'event' ||
        ((selection.kind === 'row' || selection.kind === 'rowRange') &&
          key !== 'ArrowUp' &&
          key !== 'ArrowDown') ||
        ((selection.kind === 'column' || selection.kind === 'columnRange') &&
          key !== 'ArrowLeft' &&
          key !== 'ArrowRight')
      )
        return false
      return true
    },
    [selection.kind]
  )

  const handleKeyboardRangeSelection = useUnsafeCallback(
    ({ key, shiftKey }: { key: string; shiftKey: boolean }) => {
      if (!canHandleKeyboardRangeSelection({ key: key, shiftKey })) return false

      setGridState((prev) => {
        if (
          prev.selection.kind === 'cells' &&
          prev.columnAnchor !== null &&
          (key === 'ArrowLeft' || key === 'ArrowRight')
        ) {
          const columnIndexes = Array.from(prev.selection.keys).map(
            (cellKey) => fromCellKey(cellKey).slotIndex
          )
          if (columnIndexes.length === 0) return prev

          const minColumnIndex = Math.min(...columnIndexes)
          const maxColumnIndex = Math.max(...columnIndexes)
          const endpoint = prev.columnAnchor === minColumnIndex ? maxColumnIndex : minColumnIndex
          const nextEndpoint = key === 'ArrowRight' ? endpoint + 1 : endpoint - 1
          if (nextEndpoint < 0 || nextEndpoint >= slots.length) return prev

          const startIndex = Math.min(prev.columnAnchor, nextEndpoint)
          const endIndex = Math.max(prev.columnAnchor, nextEndpoint)

          if (startIndex === endIndex) {
            const columnSelection = buildColumnSelection({
              columnIndex: nextEndpoint,
              rows,
              selectableRowCount,
              isCellSelectable: checkCellSelectable,
            })
            if (!columnSelection) return prev

            return {
              ...prev,
              selection: columnSelection.selection,
            }
          }

          return {
            ...prev,
            selection: { kind: 'columnRange', keys: buildColumnRangeKeys(startIndex, endIndex) },
          }
        }

        if (prev.selection.kind === 'cells') {
          const endpoint = getCellRangeEndpoint({
            selection: prev.selection,
            rowIndexByEntityId,
            rows,
          })
          if (!endpoint) return prev

          const endpointRowIndex = rowIndexByEntityId.get(endpoint.entityId)
          if (endpointRowIndex === undefined) return prev

          const nextSlotIndex =
            key === 'ArrowRight'
              ? endpoint.slotIndex + 1
              : key === 'ArrowLeft'
                ? endpoint.slotIndex - 1
                : endpoint.slotIndex
          const nextRowIndex =
            key === 'ArrowDown'
              ? endpointRowIndex + 1
              : key === 'ArrowUp'
                ? endpointRowIndex - 1
                : endpointRowIndex
          const nextRow = rows[nextRowIndex]

          if (!nextRow || nextSlotIndex < 0 || nextSlotIndex >= slots.length) return prev
          onKeyboardEndpointMoved?.({ rowIndex: nextRowIndex })

          return {
            ...prev,
            selection: buildCellRangeSelection({
              anchorCell: prev.selection.anchor,
              targetCell: { entityId: nextRow.entity.id, slotIndex: nextSlotIndex },
              append: false,
              previousSelection: prev.selection,
              rows,
              rowIndexByEntityId,
              isCellSelectable: checkCellSelectable,
            }),
          }
        }

        if (prev.selection.kind === 'row' || prev.selection.kind === 'rowRange') {
          if (key !== 'ArrowUp' && key !== 'ArrowDown') return prev

          const endpoint = getRowRangeEndpoint({
            selection: prev.selection,
            rowAnchor: prev.rowAnchor,
            rowIndexByEntityId,
          })
          if (endpoint === null || prev.rowAnchor === null) return prev

          const nextEndpoint = key === 'ArrowDown' ? endpoint + 1 : endpoint - 1
          const nextRow = rows[nextEndpoint]
          if (!nextRow || !isRowSelectable(nextRow)) return prev
          onKeyboardEndpointMoved?.({ rowIndex: nextEndpoint })

          const startIndex = Math.min(prev.rowAnchor, nextEndpoint)
          const endIndex = Math.max(prev.rowAnchor, nextEndpoint)

          return {
            ...prev,
            selection:
              startIndex === endIndex
                ? { kind: 'row', rowId: nextRow.entity.id }
                : { kind: 'rowRange', keys: buildRowRangeKeys(startIndex, endIndex, rows) },
          }
        }

        if (prev.selection.kind === 'column' || prev.selection.kind === 'columnRange') {
          if (key !== 'ArrowLeft' && key !== 'ArrowRight') return prev

          const endpoint = getColumnRangeEndpoint({
            selection: prev.selection,
            columnAnchor: prev.columnAnchor,
          })
          if (endpoint === null || prev.columnAnchor === null) return prev

          const nextEndpoint = key === 'ArrowRight' ? endpoint + 1 : endpoint - 1
          if (nextEndpoint < 0 || nextEndpoint >= slots.length) return prev

          const startIndex = Math.min(prev.columnAnchor, nextEndpoint)
          const endIndex = Math.max(prev.columnAnchor, nextEndpoint)

          if (startIndex === endIndex) {
            const columnSelection = buildColumnSelection({
              columnIndex: nextEndpoint,
              rows,
              selectableRowCount,
              isCellSelectable: checkCellSelectable,
            })
            if (!columnSelection) return prev

            return {
              ...prev,
              selection: columnSelection.selection,
            }
          }

          return {
            ...prev,
            selection: { kind: 'columnRange', keys: buildColumnRangeKeys(startIndex, endIndex) },
          }
        }

        return prev
      })

      return true
    },
    [
      canHandleKeyboardRangeSelection,
      checkCellSelectable,
      onKeyboardEndpointMoved,
      rowIndexByEntityId,
      rows,
      selectableRowCount,
      slots.length,
    ]
  )

  const handleCornerCellSelected = useUnsafeCallback(() => {
    setSelection((prev) => {
      if (!canSelectAll) return { kind: 'none' }
      return prev.kind === 'all' ? { kind: 'none' } : { kind: 'all' }
    })
  }, [canSelectAll, setSelection])

  return {
    selection,
    selectedCellKeys,
    selectedRowIds,
    selectedColumnIndexes,
    selectedEventKeys,
    clearSelection,
    handleCellSelected,
    handleRowHeaderSelected,
    handleColumnHeaderSelected,
    handleHeaderDragStart,
    handleKeyboardRangeSelection,
    handleCornerCellSelected,
    isAllSelected,
    handleEventSelected,
    fromCellKey,
  }
}

export { useGridSelection }
